import {
  fieldMappingGpsQuality,
  FIELD_MAPPING_MAX_GPS_ACCURACY_M,
} from '../../utils/fieldMappingGps';

interface GpsAccuracyPillProps {
  accuracyM: number | null | undefined;
  /** Trace vertices skipped for poor GPS (trace mode only). */
  skippedCount?: number;
  className?: string;
}

const QUALITY_TEXT: Record<ReturnType<typeof fieldMappingGpsQuality>, string> = {
  good: 'text-emerald-700 dark:text-emerald-400',
  fair: 'text-amber-700 dark:text-amber-400',
  poor: 'text-rose-700 dark:text-rose-400',
  unknown: 'text-gray-500 dark:text-gray-400',
};

/** Compact GPS accuracy pill — matches ZoomScalePill styling. */
export function GpsAccuracyPill({
  accuracyM,
  skippedCount = 0,
  className = '',
}: GpsAccuracyPillProps) {
  const quality = fieldMappingGpsQuality(accuracyM);
  const hasAccuracy = accuracyM != null && Number.isFinite(accuracyM) && accuracyM > 0;
  const label = hasAccuracy ? `±${Math.round(accuracyM!)} m` : '…';
  const qualityClass = QUALITY_TEXT[quality];

  return (
    <div
      className={`pointer-events-none select-none ${className}`}
      aria-live="polite"
      aria-label={
        hasAccuracy
          ? `GPS accuracy ${Math.round(accuracyM!)} meters${skippedCount > 0 ? `, ${skippedCount} points skipped` : ''}`
          : 'GPS accuracy acquiring'
      }
      title={`Phone-reported GPS uncertainty (95% radius). Trace points above ${FIELD_MAPPING_MAX_GPS_ACCURACY_M} m are skipped.`}
    >
      <div className="inline-flex h-[22px] max-w-[min(100vw-6rem,11rem)] items-center gap-1 rounded-full bg-white/92 px-1.5 text-[10px] font-mono shadow-sm backdrop-blur-sm dark:bg-gray-800/92">
        <span className="font-semibold text-gray-600 dark:text-gray-300">GPS</span>
        <span className="h-2.5 w-px bg-gray-300/80 dark:bg-gray-600" aria-hidden />
        <span className={`whitespace-nowrap font-semibold tabular-nums ${qualityClass}`}>{label}</span>
        {skippedCount > 0 ? (
          <>
            <span className="h-2.5 w-px bg-gray-300/80 dark:bg-gray-600" aria-hidden />
            <span className="whitespace-nowrap text-[9px] text-gray-500 dark:text-gray-400">
              {skippedCount} skip
            </span>
          </>
        ) : null}
      </div>
    </div>
  );
}
